import { PagedRequest } from 'common/models/data/pagedRequest.model';
import { FAILURE_HEADER, SEARCH_DEBOUNCE_DELAY, defaultPageRequest } from 'common/config';
import { Dispatch, MutableRefObject, SetStateAction } from 'react';
import { FieldError } from 'common/models';
import { addValidationError } from 'common/utils/error';
import { CompanyInfoField } from '../enums/data-management.enum';
import {
    DESCRIPTION_EMPTY_ERROR,
    GET_EY_FOOTPRINT_INFO_FAILED,
    SOURCE_NAME_EMPTY_ERROR,
    YEAR_EMPTY_ERROR,
    YEAR_SHORT_DATE_FORMAT_ERROR
} from '../config';
import { UpdateCompanyInfoPayload } from '../models/data-management.model';
import { appStateActions } from 'common/redux/app-state';
import { AppDispatch } from 'common/redux/core/store.config';
import { DataManagementService } from '../redux/data-management.service';
import {
    COMPANY_INFO_EDIT_FAIL,
    COMPANY_INFO_EDIT_SUCCESS,
    SUCCESS_HEADER
} from 'common/config/message.config';
import { MessageType } from 'common/enums';

export const searchChangeHandlerUtil = (
    event: React.ChangeEvent<HTMLInputElement>,
    setSearchVal: Dispatch<SetStateAction<string>>,
    setPagedRequest: Dispatch<SetStateAction<PagedRequest>>,
    timer: MutableRefObject<null | ReturnType<typeof setTimeout>>
) => {
    setSearchVal(event.target.value);
    if (event.target.value.length === 0) {
        if (timer.current) {
            clearTimeout(timer.current);
        }
        setPagedRequest(defaultPageRequest);
    }
    if (event.target.value.length >= 3) {
        if (timer.current) {
            clearTimeout(timer.current);
        }
        timer.current = setTimeout(() => {
            const updatedPagedRequest: PagedRequest = {
                ...defaultPageRequest,
                searchText: event.target.value
            };
            setPagedRequest(updatedPagedRequest);
        }, SEARCH_DEBOUNCE_DELAY);
    }
};

export const handleEYCompanyFootprintInfoUpdate = async (
    fieldErrors: FieldError[],
    setFieldErrors: Dispatch<SetStateAction<FieldError[]>>,
    { source, year, companyId, description }: UpdateCompanyInfoPayload,
    dispatch: AppDispatch,
    onClose: () => void
) => {
    let errors = fieldErrors;
    const regex = /^\d{2}-[A-Za-z]{3}$/;
    if (!source.trim()) {
        errors = addValidationError(errors, CompanyInfoField.Source, SOURCE_NAME_EMPTY_ERROR);
    }
    if (!year || year == null) {
        errors = addValidationError(errors, CompanyInfoField.Year, YEAR_EMPTY_ERROR);
    }
    if (!regex.test(year)) {
        errors = addValidationError(errors, CompanyInfoField.Year, YEAR_SHORT_DATE_FORMAT_ERROR);
    }
    if (!description.trim()) {
        errors = addValidationError(errors, CompanyInfoField.Description, DESCRIPTION_EMPTY_ERROR);
    }
    if (errors.length === 0) {
        if (source && year && companyId && description) {
            const updatedData: UpdateCompanyInfoPayload = {
                companyId: companyId,
                source: source,
                year: year,
                description: description
            };
            dispatch({
                type: appStateActions.showLoader.type,
                payload: true
            });
            const response = await DataManagementService.updateCompanyInfo(updatedData);
            if (response?.hasErrors && response?.errors) {
                dispatch({
                    type: appStateActions.showLoader.type,
                    payload: false
                });
                dispatch({
                    type: appStateActions.showMessageModal.type,
                    payload: {
                        isVisible: true,
                        header: FAILURE_HEADER,
                        message: COMPANY_INFO_EDIT_FAIL,
                        type: MessageType.Error
                    }
                });
            } else {
                if (response?.value) {
                    onClose();
                    dispatch({
                        type: appStateActions.showLoader.type,
                        payload: false
                    });
                    dispatch({
                        type: appStateActions.showMessageModal.type,
                        payload: {
                            isVisible: true,
                            header: SUCCESS_HEADER,
                            message: COMPANY_INFO_EDIT_SUCCESS,
                            type: MessageType.Success
                        }
                    });
                } else {
                    dispatch({
                        type: appStateActions.showLoader.type,
                        payload: false
                    });
                    dispatch({
                        type: appStateActions.showMessageModal.type,
                        payload: {
                            isVisible: true,
                            header: FAILURE_HEADER,
                            message: COMPANY_INFO_EDIT_FAIL,
                            type: MessageType.Error
                        }
                    });
                }
            }
        }
    }
    setFieldErrors(errors);
};

export const getEYCompanyFootprintInfo = async (
    fieldErrors: FieldError[],
    setFieldErrors: Dispatch<SetStateAction<FieldError[]>>,
    setSource: Dispatch<SetStateAction<string>>,
    setYear: Dispatch<SetStateAction<string>>,
    setDescription: Dispatch<SetStateAction<string>>,
    companyInfoResponse: MutableRefObject<null | UpdateCompanyInfoPayload>,
    dispatch: AppDispatch
) => {
    dispatch({
        type: appStateActions.showLoader.type,
        payload: true
    });
    const infoResponse = await DataManagementService.getCompanyInfo();
    if (infoResponse?.hasErrors && infoResponse?.errors) {
        setFieldErrors([
            ...fieldErrors,
            { field: CompanyInfoField.Modal, message: GET_EY_FOOTPRINT_INFO_FAILED }
        ]);
        dispatch({
            type: appStateActions.showLoader.type,
            payload: false
        });
    } else {
        if (infoResponse?.value) {
            setSource(infoResponse?.value?.source || '');
            // Year format should be yy-MMM ex:20-Nov
            const regex = /^\d{2}-[A-Za-z]{3}$/;
            if (regex.test(infoResponse?.value?.year)) {
                setYear(infoResponse?.value?.year || '');
            } else {
                setFieldErrors([
                    ...fieldErrors,
                    { field: CompanyInfoField.Year, message: YEAR_SHORT_DATE_FORMAT_ERROR }
                ]);
            }
            setDescription(infoResponse?.value?.description || '');
            companyInfoResponse.current = infoResponse?.value;
        } else {
            setFieldErrors([
                ...fieldErrors,
                { field: CompanyInfoField.Modal, message: GET_EY_FOOTPRINT_INFO_FAILED }
            ]);
        }
        dispatch({
            type: appStateActions.showLoader.type,
            payload: false
        });
    }
};
